// apps/web/src/components/painel/home/heroi-metrica.tsx — Fase 17.8, Planos 01 e 04 (D-06; UI-SPEC U-04, U-26, U-27).
// Métrica herói da home (data-testid="painel-heroi"): rótulo com ponto do perfil, informação de medição e link "Ver lista";
// numeral (NumeroAoVivo) com unidade; faixa contra a média dos 14 dias úteis; sparkline. Zero medido, indisponível e sem
// medição são apresentações distintas (0 / Indisponível / — Sem medição); o carregamento usa esqueleto, nunca spinner.
// Só pesos 400/600, tamanhos por token e espaçamentos da escala; a cor vem de --perfil-* (CSS), nunca de matiz solto.
import { useId } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpRight, Minus, RotateCcw } from 'lucide-react';
import type { Comparacao, FaixaHeroi, MetricaHeroi, UnidadeMetrica } from '@pieltec/contracts';
import type { AtributosBrilho } from '../../../lib/painel/home/brilho';
import { CATALOGO_METRICAS } from '../../../lib/painel/home/catalogo-metricas';
import { formatarMoedaCompacta, formatarNumero, formatarPontosPercentuais, mesAnterior, nomeDoMes } from '../../../lib/painel/home/formato';
import { textoDoMotivoSemMedicao } from '../../../lib/painel/home/motivo-sem-medicao';
import { tomDaTendencia } from '../../../lib/painel/home/polaridade';
import { podeAbrirRota } from '../../../lib/painel/blocos';
import { Button } from '../../ui/button';
import { Skeleton } from '../../ui/skeleton';
import { NumeroAoVivo, type OrigemNumero } from './numero-ao-vivo';
import { PopoverMedicao } from './popover-medicao';
import { Sparkline } from './sparkline';
import '../../../styles/painel-home.css';

const ROTULO_UNIDADE: Partial<Record<UnidadeMetrica, string>> = {
  m2: 'm²',
  kg: 'kg',
  pct: '%',
  pc: 'pç',
  ops: 'OPs',
  pvs: 'PVs',
  notas: 'notas',
  pedidos: 'pedidos',
  expedicoes: 'expedições',
  ncs: 'NCs',
  clientes: 'clientes',
};

/** Singular das unidades de contagem que concordam com o número (1 PV, 1 nota); as demais (OPs, m², %) não mudam. */
const ROTULO_UNIDADE_SINGULAR: Partial<Record<UnidadeMetrica, string>> = { pvs: 'PV', notas: 'nota', pedidos: 'pedido', expedicoes: 'expedição', ncs: 'NC', clientes: 'cliente' };

/** Texto da unidade ao lado do numeral (m², kg, %...); unidades sem rótulo próprio usam o id da unidade. Com `valor` 1, concorda no singular. */
export function rotuloDaUnidade(unidade: UnidadeMetrica, valor?: number): string {
  if (valor === 1) return ROTULO_UNIDADE_SINGULAR[unidade] ?? ROTULO_UNIDADE[unidade] ?? unidade;
  return ROTULO_UNIDADE[unidade] ?? unidade;
}

export interface HeroiMetricaProps {
  /** Rótulo do indicador (ex.: "Área pintada hoje"). */
  readonly rotulo: string;
  /** Métrica da API; `undefined` enquanto a carga não terminou. */
  readonly metrica: MetricaHeroi | undefined;
  readonly carregando: boolean;
  /** Rota do "Ver lista"; sem ela usa o destino do catálogo da métrica. Só aparece se a sessão puder abrir a rota. */
  readonly destino?: string;
  readonly permissoes?: readonly string[];
  /** Verdadeiro na primeira carga da aba: contagem do numeral, desenho da sparkline e entrada da faixa. */
  readonly primeiraCarga?: boolean;
  /** Recarrega só esta região (botão exibido quando o indicador está indisponível). */
  readonly onTentarNovamente?: () => void;
  /** Origem da mudança do valor (rolo só em 'ao-vivo'); sem ela vale `primeiraCarga`. */
  readonly origem?: OrigemNumero;
  /** Marcação do brilho de ~1 s (`data-brilho`) ou do contorno estático (movimento reduzido). */
  readonly brilho?: AtributosBrilho;
}

function Apoio({ children, testid }: { readonly children: string; readonly testid?: string }): ReactElement {
  return <p data-testid={testid} className="min-w-0 break-words text-[length:var(--font-label)] font-normal leading-[1.4] text-muted-foreground">{children}</p>;
}

/** Percentual do valor sobre a referência (média); referência zero ou inválida não tem faixa. */
export function percentualDaMedia(valor: number, referencia: number): number | null {
  if (!Number.isFinite(valor) || !Number.isFinite(referencia) || referencia <= 0) return null;
  return Math.round((valor / referencia) * 100);
}

/** Fração do trilho preenchida (0 a 1, três casas), limitada a 100%. */
export function fracaoDaFaixa(valor: number, referencia: number): number {
  if (!Number.isFinite(valor) || !Number.isFinite(referencia) || referencia <= 0 || valor <= 0) return 0;
  return Math.min(1, Math.round((valor / referencia) * 1000) / 1000);
}

/**
 * Faixa do faturado no mês (U-18): barra = faturado do mês sobre o mesmo ponto do mês anterior (limitada a 100%) e as duas
 * legendas em R$ compacto. Sem o mesmo ponto do mês anterior (nada aprovado até lá) só a primeira legenda aparece, sem barra.
 */
function FaixaFaturado({ faixa, primeiraCarga }: { readonly faixa: Extract<FaixaHeroi, { tipo: 'faturado-mes' }>; readonly primeiraCarga: boolean }): ReactElement {
  const referencia = faixa.mesmoPontoMesAnterior === undefined ? null : Number(faixa.mesmoPontoMesAnterior);
  return (
    <div className="painel-faixa" data-testid="painel-heroi-faixa-faturado">
      {referencia !== null && (
        <div className="painel-faixa__barra" aria-hidden="true">
          <div className="painel-faixa__trilho">
            <div className="painel-faixa__preenchimento" style={{ transform: `scaleX(${fracaoDaFaixa(Number(faixa.faturadoMes), referencia)})` }}>
              <div className={primeiraCarga ? 'painel-faixa__gradiente painel-barra-entrada' : 'painel-faixa__gradiente'} />
            </div>
          </div>
          <span className="painel-faixa__referencia" />
        </div>
      )}
      <div className="painel-faixa__legendas">
        <span>{`${formatarMoedaCompacta(faixa.faturadoMes)} faturados em ${nomeDoMes(faixa.mesReferencia)}`}</span>
        {faixa.mesmoPontoMesAnterior !== undefined && (
          <span>{`mesmo ponto de ${nomeDoMes(mesAnterior(faixa.mesReferencia))}: ${formatarMoedaCompacta(faixa.mesmoPontoMesAnterior)}`}</span>
        )}
      </div>
    </div>
  );
}

/**
 * Faixa de comparação em texto (U-20, Qualidade): sem barra, uma linha "7 dias anteriores: 2,2%" e a tendência em pontos percentuais,
 * no tom que a polaridade da métrica decide (cair é bom no retrabalho). Só existe com comparação contra a janela anterior.
 */
function FaixaComparacaoTexto({ comparacao, dias, unidade }: { readonly comparacao: Comparacao; readonly dias: number; readonly unidade: string }): ReactElement | null {
  const referencia = Number(comparacao.referencia);
  if (!Number.isFinite(referencia)) return null;
  const variacao = comparacao.variacao;
  const tom = variacao === undefined ? 'neutra' : tomDaTendencia(variacao, comparacao.polaridade);
  const Icone = variacao === undefined || Math.round(variacao * 10) === 0 ? Minus : variacao > 0 ? ArrowUp : ArrowDown;
  const sufixo = unidade === '%' ? '%' : ` ${unidade}`;
  return (
    <div className="painel-faixa painel-faixa--texto" data-testid="painel-heroi-faixa-texto">
      <span className="painel-faixa__comparacao">{`${dias} ${dias === 1 ? 'dia anterior' : 'dias anteriores'}: ${formatarNumero(referencia, 1)}${sufixo}`}</span>
      {variacao !== undefined && (
        <span className={`painel-kv__tendencia painel-kv__tendencia--${tom}`} data-testid="painel-heroi-tendencia" data-tom={tom}>
          <Icone className="size-3" aria-hidden="true" />
          <span>{formatarPontosPercentuais(variacao)}</span>
          <span className="sr-only">{`em relação aos ${dias} dias anteriores`}</span>
        </span>
      )}
    </div>
  );
}

/** Total de um segmento da faixa por prazo; id ausente, número ilegível ou negativo vale 0. */
function totalDoSegmento(faixa: Extract<FaixaHeroi, { tipo: 'segmentos' }>, id: string): number {
  const total = Number(faixa.segmentos.find((segmento) => segmento.id === id)?.total);
  return Number.isFinite(total) && total > 0 ? Math.trunc(total) : 0;
}

/**
 * Faixa segmentada por prazo (U-21, Compras): uma barra de segmentos proporcionais aos totais (no prazo no gradiente do perfil, vencem em
 * 7 dias hachurado em --color-subtle e atrasados, o único segmento vermelho, em --color-destructive) e a legenda com as contagens. A
 * barra é decorativa (aria-hidden): a legenda é o texto lido, então a cor nunca é o único sinal. Segmento zerado não vira trecho da barra e
 * sem nenhum pedido em aberto não há faixa.
 */
function FaixaSegmentos({ faixa }: { readonly faixa: Extract<FaixaHeroi, { tipo: 'segmentos' }> }): ReactElement | null {
  const noPrazo = totalDoSegmento(faixa, 'no-prazo');
  const vencem = totalDoSegmento(faixa, 'vencem-7-dias');
  const atrasados = totalDoSegmento(faixa, 'atrasados');
  if (noPrazo + vencem + atrasados === 0) return null;
  const partes = [
    { id: 'no-prazo', classe: 'no-prazo', total: noPrazo },
    { id: 'vencem-7-dias', classe: 'vencem', total: vencem },
    { id: 'atrasados', classe: 'atrasados', total: atrasados },
  ].filter((parte) => parte.total > 0);
  const legenda = [
    `${formatarNumero(noPrazo, 0)} no prazo`,
    `${formatarNumero(vencem, 0)} ${vencem === 1 ? 'vence' : 'vencem'} em 7 dias`,
    `${formatarNumero(atrasados, 0)} ${atrasados === 1 ? 'atrasado' : 'atrasados'}`,
  ].join(' · ');
  return (
    <div className="painel-faixa" data-testid="painel-heroi-faixa-segmentos">
      <div className="painel-faixa__segmentos" aria-hidden="true">
        {partes.map((parte) => (
          <span key={parte.id} data-segmento={parte.id} className={`painel-faixa__segmento painel-faixa__segmento--${parte.classe}`} style={{ flexGrow: parte.total, flexBasis: 0 }} />
        ))}
      </div>
      <div className="painel-faixa__legendas">
        <span>{legenda}</span>
      </div>
    </div>
  );
}

/** Valor de uma parte da projeção; ilegível, zero ou negativo não ocupa trecho da barra. */
function valorDaParte(faixa: Extract<FaixaHeroi, { tipo: 'projecao' }>, id: 'faturado' | 'pronto' | 'em-producao'): number {
  const valor = Number(faixa.partes.find((parte) => parte.id === id)?.valor);
  return Number.isFinite(valor) && valor > 0 ? valor : 0;
}

/**
 * Barra de projeção do mês (U-22, D-11): uma barra segmentada de 10px (raio 6px, vão de 4px) com três segmentos de largura proporcional
 * ao valor (flex-grow): faturado (gradiente do perfil), pronto para faturar (--tag-faturar, o token emerald) e saldo em produção
 * (hachurado, --color-subtle). A legenda dá o valor de cada parte em R$ compacto (Body 600) e, abaixo, "projeção do mês" à esquerda e
 * "{mês anterior} fechou em" à direita. Só soma de partes: nenhuma estimativa estatística. A barra é decorativa (aria-hidden): a
 * legenda é o texto lido, então a cor nunca é o único sinal.
 */
function FaixaProjecao({ faixa, mesReferencia }: { readonly faixa: Extract<FaixaHeroi, { tipo: 'projecao' }>; readonly mesReferencia: string }): ReactElement {
  const partes = [
    { id: 'faturado' as const, classe: 'faturado', rotulo: 'faturado' },
    { id: 'pronto' as const, classe: 'pronto', rotulo: 'pronto para faturar' },
    { id: 'em-producao' as const, classe: 'producao', rotulo: 'em produção' },
  ].map((parte) => ({ ...parte, total: valorDaParte(faixa, parte.id), texto: formatarMoedaCompacta(faixa.partes.find((candidata) => candidata.id === parte.id)?.valor ?? '0') }));
  const trechos = partes.filter((parte) => parte.total > 0);
  return (
    <div className="painel-faixa" data-testid="painel-heroi-faixa-projecao">
      {trechos.length > 0 && (
        <div className="painel-faixa__segmentos" aria-hidden="true">
          {trechos.map((parte) => (
            <span key={parte.id} data-segmento={parte.id} className={`painel-faixa__segmento painel-faixa__segmento--${parte.classe}`} style={{ flexGrow: parte.total, flexBasis: 0 }} />
          ))}
        </div>
      )}
      <div className="painel-faixa__legendas painel-faixa__legendas--partes">
        {partes.map((parte) => (
          <span key={parte.id} className="painel-faixa__parte">
            <b>{parte.texto}</b> {parte.rotulo}
          </span>
        ))}
      </div>
      <div className="painel-faixa__legendas">
        <span>
          projeção do mês <b>{formatarMoedaCompacta(faixa.projecao)}</b>
        </span>
        {faixa.mesAnteriorFechou !== undefined && (
          <span>
            {nomeDoMes(mesAnterior(mesReferencia))} fechou em <b>{formatarMoedaCompacta(faixa.mesAnteriorFechou)}</b>
          </span>
        )}
      </div>
    </div>
  );
}

function CabecalhoDoRotulo({ idRotulo, rotulo, children }: { readonly idRotulo: string; readonly rotulo: string; readonly children?: ReactNode }): ReactElement {
  return (
    <div className="painel-heroi__linha-rotulo">
      <span className="painel-heroi__ponto" aria-hidden="true" />
      <h2 id={idRotulo} className="painel-heroi__rotulo">{rotulo}</h2>
      {children !== undefined && <div className="painel-heroi__acoes">{children}</div>}
    </div>
  );
}

export function HeroiMetrica({ rotulo, metrica, carregando, destino, permissoes = [], primeiraCarga = true, onTentarNovamente, origem: origemDaMudanca, brilho = {} }: HeroiMetricaProps): ReactElement {
  const idRotulo = useId();
  const entrada = primeiraCarga ? 'painel-entrada painel-entrada-3' : '';
  const secao = `painel-heroi ${entrada}`.trim();

  if (carregando || metrica === undefined) {
    return (
      <section data-testid="painel-heroi" aria-labelledby={idRotulo} aria-busy="true" className={`${secao} painel-heroi--carregando`}>
        <CabecalhoDoRotulo idRotulo={idRotulo} rotulo={rotulo} />
        <Skeleton className="h-16 w-48" />
        <Skeleton className="h-4 w-64" />
      </section>
    );
  }

  if (metrica.estado === 'INDISPONIVEL') {
    return (
      <section data-testid="painel-heroi" aria-labelledby={idRotulo} className={secao}>
        <CabecalhoDoRotulo idRotulo={idRotulo} rotulo={rotulo} />
        <p className="text-[length:var(--font-body)] font-semibold leading-normal text-muted-foreground">Indisponível</p>
        <Apoio>Não foi possível medir este indicador.</Apoio>
        {onTentarNovamente !== undefined && (
          <Button type="button" variant="outline" size="sm" className="self-start [[data-density=comfortable]_&]:h-11" onClick={onTentarNovamente} data-testid="painel-regiao-heroi-retry">
            <RotateCcw aria-hidden="true" />
            <span>Tentar novamente</span>
          </Button>
        )}
      </section>
    );
  }

  if (metrica.estado === 'SEM_MEDICAO') {
    // O motivo da API vira uma linha calma abaixo do estado (D3 da UAT): diz por que não há número.
    const motivo = textoDoMotivoSemMedicao(metrica.motivo);
    return (
      <section data-testid="painel-heroi" aria-labelledby={idRotulo} className={secao}>
        <CabecalhoDoRotulo idRotulo={idRotulo} rotulo={rotulo} />
        <p className="painel-heroi__numero painel-heroi__numero--vazio">—</p>
        <Apoio>Sem medição</Apoio>
        {motivo !== undefined && <Apoio testid="painel-heroi-motivo">{motivo}</Apoio>}
      </section>
    );
  }

  const valor = typeof metrica.valor === 'number' ? metrica.valor : Number(metrica.valor);
  const entradaCatalogo = CATALOGO_METRICAS[metrica.id];
  const rota = destino ?? entradaCatalogo?.destino;
  const podeVerLista = rota !== undefined && podeAbrirRota(rota, permissoes);
  const porPreco = metrica.cobertura?.criterio === 'itens-com-preco';
  const semApontamentos = !porPreco && metrica.cobertura?.total === 0;
  const coberturaParcial = metrica.cobertura !== undefined && metrica.cobertura.total > 0 && metrica.cobertura.base < metrica.cobertura.total;
  const comparacao = metrica.comparacao;
  const referencia = comparacao?.tipo === 'MEDIA_DIAS_UTEIS' ? Number(comparacao.referencia) : null;
  const percentual = referencia === null ? null : percentualDaMedia(valor, referencia);
  // Entregas do dia (Almoxarifado): acompanham a porcentagem da média na legenda da faixa.
  const entregasBrutas = metrica.secundarios?.find((secundario) => secundario.id === 'entregas')?.valor;
  const entregas = entregasBrutas === undefined || !Number.isFinite(Number(entregasBrutas)) ? undefined : Number(entregasBrutas);
  const dinheiro = metrica.unidade === 'BRL';
  const unidade = rotuloDaUnidade(metrica.unidade, valor);
  const origem: OrigemNumero = origemDaMudanca ?? (primeiraCarga ? 'primeira-carga' : 'silenciosa');

  return (
    <section data-testid="painel-heroi" aria-labelledby={idRotulo} className={secao} {...brilho}>
      <CabecalhoDoRotulo idRotulo={idRotulo} rotulo={rotulo}>
        <>
          <PopoverMedicao
            rotulo={rotulo}
            definicao={entradaCatalogo?.definicao ?? 'Valor medido no período indicado.'}
            periodo={metrica.periodo}
            cobertura={metrica.cobertura}
          />
          {podeVerLista && rota !== undefined && (
            <a href={rota} className="painel-heroi__ver-lista" data-testid="painel-heroi-ver-lista">
              <span>Ver lista</span>
              <ArrowUpRight className="size-3" aria-hidden="true" />
            </a>
          )}
        </>
      </CabecalhoDoRotulo>
      <p className="painel-heroi__numero">
        {dinheiro && <span className="painel-heroi__unidade painel-heroi__unidade--prefixo">R$</span>}
        <NumeroAoVivo valor={valor} formatar={(v) => formatarNumero(v, dinheiro ? 0 : 1)} origem={origem} />
        {!dinheiro && <span className="painel-heroi__unidade">{unidade}</span>}
      </p>
      {semApontamentos && <Apoio>Ainda sem apontamentos hoje.</Apoio>}
      {coberturaParcial && metrica.cobertura && (
        <Apoio>
          {porPreco
            ? `${metrica.cobertura.base} de ${metrica.cobertura.total} itens com preço no pedido.`
            : `${metrica.cobertura.base} de ${metrica.cobertura.total} apontamentos com área cadastrada.`}
        </Apoio>
      )}
      {referencia !== null && percentual !== null && (
        <div className="painel-faixa" data-testid="painel-heroi-faixa">
          <div className="painel-faixa__barra" aria-hidden="true">
            <div className="painel-faixa__trilho">
              <div className="painel-faixa__preenchimento" style={{ transform: `scaleX(${fracaoDaFaixa(valor, referencia)})` }}>
                <div className={primeiraCarga ? 'painel-faixa__gradiente painel-barra-entrada' : 'painel-faixa__gradiente'} />
              </div>
            </div>
            <span className="painel-faixa__referencia" />
          </div>
          <div className="painel-faixa__legendas">
            <span>{entregas === undefined ? `${formatarNumero(percentual, 0)}% da média diária` : `${formatarNumero(entregas, 0)} ${entregas === 1 ? 'entrega' : 'entregas'} · ${formatarNumero(percentual, 0)}% da média diária`}</span>
            <span>{dinheiro ? `média R$ ${formatarNumero(referencia, 0)}` : `média ${formatarNumero(referencia, 1)} ${unidade}`}</span>
          </div>
        </div>
      )}
      {metrica.faixa?.tipo === 'comparacao-texto' && comparacao?.tipo === 'JANELA_ANTERIOR' && (
        <FaixaComparacaoTexto comparacao={comparacao} dias={metrica.periodo.dias ?? 7} unidade={unidade} />
      )}
      {metrica.faixa?.tipo === 'faturado-mes' && <FaixaFaturado faixa={metrica.faixa} primeiraCarga={primeiraCarga} />}
      {metrica.faixa?.tipo === 'segmentos' && <FaixaSegmentos faixa={metrica.faixa} />}
      {metrica.faixa?.tipo === 'projecao' && <FaixaProjecao faixa={metrica.faixa} mesReferencia={metrica.periodo.inicio.slice(0, 7)} />}
      {metrica.serie !== undefined && <Sparkline serie={metrica.serie} rotulo={entradaCatalogo?.rotuloSerie} primeiraCarga={primeiraCarga} />}
    </section>
  );
}
